/**
 * Toimikud (D21) — the shared container for one problem or one piece of work.
 *
 * Before this, a conversation belonged to exactly one person and nothing else.
 * Two people working the same Directo field problem could not see each other's
 * threads, and neither could hand the topic to a new conversation. A toimik holds
 * the context, the timeline, the threads and the files in one place, and its summary
 * is injected into every conversation opened inside it.
 */

export const CASE_STATUSES = ["open", "resolved", "archived"] as const;

export type CaseStatus = (typeof CASE_STATUSES)[number];

/** mAImu's own entries are labelled. A note that reads as a colleague's when the
 *  assistant wrote it is worse than no note. */
export const AUTHOR_KINDS = ["user", "maimu"] as const;

export type AuthorKind = (typeof AUTHOR_KINDS)[number];

export interface CaseListItem {
  id: string;
  code: string | null;
  title: string;
  status: CaseStatus;
  members: string[];
  conversations: number;
  notes: number;
  files: number;
  createdBy: string | null;
  updatedAt: string;
  /** False only for an admin looking at a toimik they have not joined. */
  member: boolean;
}

export interface CaseNoteEntry {
  id: string;
  body: string;
  authorKind: AuthorKind;
  /** Username, or null for mAImu and for a deleted account. */
  author: string | null;
  conversationId: string | null;
  createdAt: string;
}

export interface CaseRevisionEntry {
  id: string;
  summary: string;
  authorKind: AuthorKind;
  author: string | null;
  createdAt: string;
}

export interface CaseFileEntry {
  id: string;
  name: string;
  contentType: string;
  bytes: number;
  addedBy: string | null;
  addedAt: string;
}

export interface CaseConversationEntry {
  id: string;
  title: string;
  /** Whose thread it is. Every member can read it; only the owner can continue it. */
  owner: string | null;
  messages: number;
  updatedAt: string;
}

/** The list carries counts, the detail carries the things themselves. */
export interface CaseDetail extends Omit<CaseListItem, "notes" | "files" | "conversations"> {
  summary: string;
  notes: CaseNoteEntry[];
  files: CaseFileEntry[];
  conversations: CaseConversationEntry[];
  revisions: number;
}

export interface CreateCaseRequest {
  title: string;
  code?: string | null;
  summary?: string;
  /** Usernames to add alongside the creator, who is always a member. */
  members?: string[];
}

export interface UpdateCaseRequest {
  title?: string;
  code?: string | null;
  summary?: string;
  status?: CaseStatus;
}

export interface AppendCaseNoteRequest {
  body: string;
}
